import { useEffect, useState } from "react";
import { addDays, format, parseISO, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertCircle, Bell, Calendar, CheckCheck, Clock,
  MessageSquare, Pencil, RotateCcw, Search, Timer,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { useSessionAlerts } from "@/hooks/useSessionAlerts";
import { useUpdateWorkSession, useFetchWorkSessions } from "@/hooks/useWorkSessions";
import { useAuth } from "@/contexts/AuthContext";
import { showErrorToast, showSuccessToast } from "@/components/ui/toast";
import WorSessionFormDialog from "@/components/forms/WorSessionFormDialog";
import type { WorkSession } from "@/types/workSession.type";

type View = "pending" | "reviewed";

// ── Helpers ───────────────────────────────────────────────────────────────────

const getInitials = (firstName?: string, lastName?: string) => {
  const f = firstName?.[0] ?? "";
  const l = lastName?.[0] ?? "";
  return (f + l).toUpperCase() || "?";
};

const formatDuration = (startTime: string, endTime: string | null) => {
  if (!endTime) return "-";
  const diff = new Date(endTime).getTime() - new Date(startTime).getTime();
  const totalMinutes = Math.floor(diff / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

// ── Composant ─────────────────────────────────────────────────────────────────

const LIMIT = 10;

export const SessionAlertsPage = () => {
  const { user } = useAuth();
  const { mutate: updateSessionMutation, isPending } = useUpdateWorkSession();

  // ── État de navigation ────────────────────────────────────────────────────
  const [view, setView] = useState<View>("pending");
  const [page, setPage] = useState(1);
  const [sessionToEdit, setSessionToEdit] = useState<WorkSession | null>(null);

  // ── Recherche avec debounce ───────────────────────────────────────────────
  // searchInput : valeur brute de l'input (change à chaque frappe)
  // search      : valeur envoyée au serveur (mise à jour 400ms après)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Date il y a 30 jours (pour limiter l'historique des sessions traitées)
  const thirtyDaysAgo = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const today = format(addDays(new Date(), 1), "yyyy-MM-dd"); // ← ajoute cette ligne


  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // retour page 1 dès qu'on cherche
    }, 400);
    return () => clearTimeout(timer); // annule le timer si on retape avant 400ms
  }, [searchInput]);

  // Reset de la page lors d'un changement de vue
  const handleViewChange = (v: View) => {
    setView(v);
    setPage(1);
    setSearchInput("");
    setSearch("");
  };

  // ── Données — sessions non traitées ──────────────────────────────────────
  const { alerts: pendingAlerts, count: pendingCount, totalPages: pendingTotalPages, isLoading: isLoadingPending } =
    useSessionAlerts({ page, limit: LIMIT, search });

  // ── Données — sessions traitées ──────────────────────────────────────────
  const { data: reviewedData, isLoading: isLoadingReviewed } = useFetchWorkSessions(
    { companyId: user?.companyId, 
    hasComment: true, 
    isReviewed: true, 
    startDate: thirtyDaysAgo, 
    endDate: today,
    page, 
    limit: LIMIT, 
    search },
    { enabled: view === "reviewed" }
  );
  const reviewedAlerts = reviewedData?.workSessions ?? [];
  const reviewedTotalPages = reviewedData?.pagination.totalPages ?? 0;
  const reviewedTotal = reviewedData?.pagination.total ?? 0;

  // Compte allégé des traitées pour la stat card (toujours visible, peu importe la vue)
  const { data: reviewedCountData } = useFetchWorkSessions({
    companyId: user?.companyId, 
    hasComment: true, 
    isReviewed: true, 
    startDate: thirtyDaysAgo, 
    endDate: today,
    limit: 1,
  });
  const reviewedCount = reviewedCountData?.pagination.total ?? 0;

  // ── Liste + pagination active ─────────────────────────────────────────────
  const activeList = view === "pending" ? pendingAlerts : reviewedAlerts;
  const activeTotalPages = view === "pending" ? pendingTotalPages : reviewedTotalPages;
  const activeTotal = view === "pending" ? pendingCount : reviewedTotal;
  const isLoading = view === "pending" ? isLoadingPending : isLoadingReviewed;

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleMarkReviewed = (sessionId: string) => {
    updateSessionMutation(
      { sessionId, data: { isReviewed: true } },
      {
        onSuccess: () => showSuccessToast("Session marquée comme traitée"),
        onError: (error) => showErrorToast(error.message),
      }
    );
  };

  const handleMarkUnreviewed = (sessionId: string) => {
    updateSessionMutation(
      { sessionId, data: { isReviewed: false } },
      {
        onSuccess: () => showSuccessToast("Session remise à traiter"),
        onError: (error) => showErrorToast(error.message),
      }
    );
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="h-6 w-6 text-primary" />
            Sessions à vérifier
        </h1>
        <p className="mt-1 text-sm text-gray-500">
            Sessions contenant un commentaire laissé par la pointeuse
        </p>
      </div>

      {/* Stat cards cliquables */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => handleViewChange("pending")}
          className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left transition-all ${
            view === "pending"
              ? "border-amber-400 bg-amber-50 ring-2 ring-amber-300"
              : "border-amber-200 bg-amber-50/40 hover:bg-amber-50"
          }`}
        >
          <div>
            <p className="text-sm font-medium text-amber-700">À traiter</p>
            <p className="mt-0.5 text-3xl font-bold text-amber-900">
              {isLoadingPending ? "—" : pendingCount}
            </p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <AlertCircle className="h-5 w-5 text-amber-500" />
          </div>
        </button>

        <button
          onClick={() => handleViewChange("reviewed")}
          className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left transition-all ${
            view === "reviewed"
              ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-300"
              : "border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50"
          }`}
        >
          <div>
            <p className="text-sm font-medium text-emerald-700">Traitées ces 30 derniers jours</p>
            <p className="mt-0.5 text-3xl font-bold text-emerald-900">{reviewedCount}</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
            <CheckCheck className="h-5 w-5 text-emerald-500" />
          </div>
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par employé ou commentaire..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        {!isLoading && (
          <span className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-500">
            {activeTotal} résultat{activeTotal > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Chargement */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-36 w-full rounded-xl" />
          ))}
        </div>
      )}

      {/* Liste vide */}
      {!isLoading && activeList.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-16 text-center">
          <CheckCheck className="mb-3 h-10 w-10 text-emerald-400" />
          <p className="font-semibold text-gray-800">
            {search ? "Aucun résultat" : view === "pending" ? "Tout est traité !" : "Aucune session traitée ces 30 derniers jours"}
          </p>
          {search && (
            <p className="mt-1 text-sm text-gray-500">
              Aucune session ne correspond à votre recherche.
            </p>
          )}
        </div>
      )}

      {/* Liste */}
      <div className="space-y-3">
        {!isLoading && activeList.map((session) => {
          const isReviewed = view === "reviewed";
          return (
            <div
              key={session.id}
              className={`rounded-xl border bg-white p-4 shadow-sm ${
                isReviewed ? "border-gray-100 opacity-90" : "border-gray-200"
              }`}
            >
              <div className="flex items-start justify-between gap-4">

                {/* Avatar + infos */}
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                    {getInitials(session.user?.firstName, session.user?.lastName)}
                  </div>
                  <div>
                    <p className="flex flex-wrap items-center gap-2 font-semibold text-gray-900">
                      {session.user?.firstName} {session.user?.lastName}
                     
                      {isReviewed && (
                        <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          <CheckCheck className="h-3 w-3" />
                          Traitée
                        </span>
                      )}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {format(parseISO(session.startTime), "EEEE d MMM yyyy", { locale: fr })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {format(parseISO(session.startTime), "HH:mm")}
                        {" → "}
                        {session.endTime ? format(parseISO(session.endTime), "HH:mm") : "—"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Timer className="h-3.5 w-3.5" />
                        Durée : {formatDuration(session.startTime, session.endTime)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Boutons */}
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => setSessionToEdit(session)}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Modifier la session
                  </button>

                  {isReviewed ? (
                    <button
                      onClick={() => handleMarkUnreviewed(session.id)}
                      disabled={isPending}
                      className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Remettre à traiter
                    </button>
                  ) : (
                    <button
                      onClick={() => handleMarkReviewed(session.id)}
                      disabled={isPending}
                      className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-600 disabled:opacity-50"
                    >
                      <CheckCheck className="h-3.5 w-3.5" />
                      Marquer comme traité
                    </button>
                  )}
                </div>
              </div>

              {/* Bloc commentaire */}
              <div className={`mt-3 border-l-2 pl-3 ${isReviewed ? "border-gray-200" : "border-amber-400"}`}>
                <p className="mb-0.5 text-xs font-medium text-gray-400">
                  <MessageSquare className="mr-1 inline h-3 w-3" />
                  Commentaire de {session.user?.firstName} {session.user?.lastName}
                  {" · "}
                  {format(parseISO(session.createdAt), "d/MM/yyyy HH:mm")}
                </p>
                <p className={`text-sm ${isReviewed ? "text-gray-400" : "text-gray-700"}`}>
                  {session.comment}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {!isLoading && activeTotalPages > 1 && (
        <PaginationControls
          page={page}
          totalPages={activeTotalPages}
          totalItems={activeTotal}
          itemsPerPage={LIMIT}
          onPageChange={setPage}
        />
      )}

      {/* Dialog de modification */}
      <WorSessionFormDialog
        open={sessionToEdit !== null}
        onOpenChange={(open) => { if (!open) setSessionToEdit(null); }}
        sessionToEdit={sessionToEdit}
        markAsReviewedOnSave={view === "pending"}
      />
    </div>
  );
};
